import React, { useContext, useMemo } from 'react';
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  HubOption,
  HUB_MAX_ICON_CONDITIONS,
} from 'dorian-shared/types/branch/HubOption';
import type { IconCondition } from 'dorian-shared/types/branch/HubOption';
import {
  MemoryDTO,
  MemoryType,
} from 'components/pages/Book/MemoryBank/memoryBankTypes';
import { useMemoryIconsContext } from 'contexts/MemoryIconsContext/MemoryIconsContext';
import { NodeEditContext } from '../../../../context/NodeEditContext';
import { useIconConditions } from '../../hooks/useIconConditions';
import { IconConditionCard } from './IconConditionCard';
import { isIconConditionValid } from './iconConditionValidation';
import './IconOverrides.scss';

export type IconOverridesProps = {
  optionUuid: string;
  iconConditions: IconCondition[];
  isDisabled: boolean;
  onChange: (changes: Partial<HubOption>) => void;
};

const MOUSE_SENSOR_OPTIONS = { activationConstraint: { distance: 5 } };
const KEYBOARD_SENSOR_OPTIONS = { coordinateGetter: sortableKeyboardCoordinates };

export function IconOverrides({
  optionUuid,
  iconConditions,
  isDisabled,
  onChange,
}: IconOverridesProps) {
  const ctx = useContext(NodeEditContext) as Record<string, unknown> | undefined;
  const memoryBankSlots = (ctx?.memoryBankSlots as MemoryDTO[] | undefined) ?? [];
  const memoryVariables = useMemo(
    () => memoryBankSlots.filter((s: MemoryDTO) => s.type === MemoryType.Number),
    [memoryBankSlots],
  );

  const { memoryIcons } = useMemoryIconsContext();

  const {
    addCondition,
    removeCondition,
    updateCondition,
    handleDragEnd,
  } = useIconConditions(iconConditions, onChange, memoryIcons);

  const sortableItems = useMemo(
    () => iconConditions.map((c) => c.uuid),
    [iconConditions],
  );

  const sensors = useSensors(
    useSensor(MouseSensor, MOUSE_SENSOR_OPTIONS),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, KEYBOARD_SENSOR_OPTIONS),
  );

  return (
    <div className="hub-option-icon-overrides" data-testid="icon-overrides">
      <p className="hub-option-icon-overrides__helper">
        First match overrides the default icon. Evaluated top-to-bottom.
      </p>

      <DndContext
        id={`icon-dnd-${optionUuid}`}
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={sortableItems} strategy={verticalListSortingStrategy}>
          <div className="list-group mb-1">
            {iconConditions.map((cond) => (
              <IconConditionCard
                key={cond.uuid}
                condition={cond}
                memoryVariables={memoryVariables}
                isDisabled={isDisabled}
                isValid={isIconConditionValid(cond)}
                onChange={(updated) => updateCondition(cond.uuid, updated)}
                onRemove={() => removeCondition(cond.uuid)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {/* Otherwise row */}
      <div className="hub-option-icon-overrides__otherwise">
        <strong className="small mr-2">Otherwise</strong>
        <small className="text-muted">uses default icon above</small>
      </div>

      {/* Add button + counter */}
      <div className="mt-2">
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={addCondition}
          disabled={isDisabled || iconConditions.length >= HUB_MAX_ICON_CONDITIONS}
        >
          + Add Icon Override
        </button>
        <small className="text-muted ml-2">
          {iconConditions.length} / {HUB_MAX_ICON_CONDITIONS}
        </small>
      </div>
    </div>
  );
}
